import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import ReferralCard from "@/components/ReferralCard";
import ServiceCatalog from "@/components/ServiceCatalog";
import RedemptionPanel from "@/components/RedemptionPanel";
import PointsHistory from "@/components/PointsHistory";
import ReferralEntriesList from "@/components/ReferralEntriesList";
import AgilityTierChip from "@/components/AgilityTierChip";
import FirstLoginTour from "@/components/FirstLoginTour";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Users, Sparkles, ClipboardList } from "lucide-react";

export default function CustomerDashboard() {
  const { user, refresh } = useAuth();
  const [refs, setRefs] = useState(null);

  const load = () => api.get("/referrals/my").then((r) => setRefs(r.data));
  useEffect(() => { load(); }, []);

  return (
    <AppShell title={`Hi, ${user?.name?.split(' ')[0]}`} subtitle="Track your rewards, share your code, redeem at partner shops." badge={<AgilityTierChip />}>
      <FirstLoginTour userId={user?.id} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* PRIMARY ACTIONS — Refer + Redemption on top */}
        <div className="lg:col-span-2 order-1">
          <ReferralCard onCommitted={load} pendingCount={refs?.pending_count ?? 0} maxPending={refs?.max_pending ?? 5} />
        </div>
        <div className="lg:col-span-1 order-2 velo-card p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute -right-10 -bottom-10 w-40 h-40 rounded-full bg-pink-500/10 blur-3xl" />
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Points balance</div>
          <div className="flex items-end gap-2 mt-3">
            <div data-testid="points-balance" className="font-mono text-5xl sm:text-6xl text-white font-light tabular-nums">{user?.points_balance ?? 0}</div>
            <div className="text-sm text-slate-400 pb-2">pts</div>
          </div>
          <div className="flex items-center gap-2 mt-3 text-sm text-emerald-400">
            <Sparkles className="w-4 h-4" strokeWidth={2} /><span>1 point = ₹1 at tie-up shops</span>
          </div>
          <div className="mt-6 pt-5 border-t border-white/10 grid grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wider">Bonus earned</div>
              <div className="text-xl sm:text-2xl text-white font-light mt-1 font-mono">{refs?.total_referral_bonus ?? 0}</div>
            </div>
            <div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wider">Closed</div>
              <div className="text-xl sm:text-2xl text-white font-light mt-1 flex items-center gap-2"><Users className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />{refs?.closed_count ?? 0}</div>
            </div>
            <div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wider">Pending</div>
              <div className="text-lg text-white font-light mt-1 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-amber-400" strokeWidth={1.5} />{refs?.pending_count ?? 0}</div>
            </div>
            <div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wider">Total shared</div>
              <div className="text-lg text-white font-light mt-1 font-mono">{(refs?.pending_count ?? 0) + (refs?.closed_count ?? 0)}</div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-3 order-3"><RedemptionPanel role="customer" onChange={refresh} /></div>

        {/* SECONDARY — history, catalogue, past referrals */}
        <div className="lg:col-span-2 order-5"><ServiceCatalog role="customer" /></div>
        <div className="lg:col-span-1 order-6"><PointsHistory /></div>
        <div className="lg:col-span-3 order-7"><ReferralEntriesList entries={refs?.referral_entries} onChange={load} /></div>
      </div>
    </AppShell>
  );
}
